import { AxiosPromise } from 'axios';
import { instance } from '.';
import { URLS } from './index';
import { Logger } from 'fsts';
import { SearchParams } from '../model/searchParams';
import { DefaultBackendHelper } from '../utils/backendHelper';
import attendee, { Attendee, Value } from '../model/attendee';
import ODataFilterBuilder from 'odata-filter-builder';
import { i18nGlobal } from '@/i18n';
import {
  ApplicationUser,
  Value as ApplicationUserValue,
} from '../model/applicationUser';
import { isEmptyId } from '../utils/generalUtils';
import { CONST } from '../utils/constants';
import { AUTHORITIES } from '../store/modules/auth';
import attendeeFixedCost, {
  AttendeeFixedCost,
} from '../model/attendeeFixedCost';

const logger = new Logger('backend.Attendee');
export interface BackendAttendee {
  getAttendeeByApplicationUserId(id: string): AxiosPromise<Attendee>;
  getAttendeeByAttendeeId(attendeeId: string): AxiosPromise<Attendee>;

  getAttendees: (
    searchParams: SearchParams,
    locationId?: string,
    departmentId?: string,
    active?: boolean,
    excludeAttendeeIds?: string[],
    attendeeIds?: string[],
    filterByMembershipTypes?: string[],
    departmentIds?: string[],
    trainerId?: string,
    attendeeRoleIds?: string[],
    memberAtDate?: string,
    applicationUserId?: string
  ) => AxiosPromise<Value>;
  getInterested: (
    searchParams: SearchParams,
    locationId?: string,
    departmentId?: string
  ) => AxiosPromise<Value>;
  getAttendeesByAppointmentId: (
    searchParams: SearchParams,
    appointmentId: string
  ) => AxiosPromise<Value>;
  getAttendeesWithoutMembership: (
    searchParams: SearchParams,
    locationId?: string,
    departmentId?: string
  ) => AxiosPromise<Value>;
  getAttendeesActiveInactive: (
    searchParams: SearchParams,
    locationId?: string,
    departmentId?: string
  ) => AxiosPromise<Value>;
  getDistinctAppUsers: (
    searchParams: SearchParams,
    onlyActive?: boolean
  ) => AxiosPromise<ApplicationUserValue>;
  getAttendeesAndInterested: (
    searchParams: SearchParams,
    locationId?: string,
    active?: boolean,
    attendeeIds?: string[],
    attendeeRoleIds?: string[],
    memberAtDate?: string
  ) => AxiosPromise<Value>;
  getAttendeesBySearch: (
    search?: string,
    locationIds?: string[],
    departmentIds?: string[],
    courseIds?: string[],
    courseFromDate?: string,
    courseTillDate?: string,
    membershipTypeIds?: string[]
  ) => AxiosPromise<Attendee[]>;

  deleteAttendee(id: string): AxiosPromise;
  deleteInterestedAttendee(id: string): AxiosPromise;

  updateAttendee(
    Attendee: Attendee,
    attendeeFixedCosts?: AttendeeFixedCost[],
    departmentIds?: string[]
  ): AxiosPromise<any>;
  updateInterestedAttendee(Attendee: Attendee): AxiosPromise<any>;

  registerAttendee(
    Attendee: ApplicationUser,
    locationId: string
  ): AxiosPromise<any>;
  registerInterestedAttendeeByEmployee(
    Attendee: Attendee,
    locationId: string
  ): AxiosPromise<any>;

  checkIfAttendeeHasFutureBookings(attendeeId: string): AxiosPromise<boolean>;
  getLocationInfoById(id: string): AxiosPromise<Location>;
  requestNewMemberNumber(): AxiosPromise<string>;

  exportAttendees(
    searchParams: SearchParams,
    culture: string,
    locationId?: string,
    departmentId?: string,
    filterByMembershipTypes?: string[],
    departmentIds?: string[]
  ): AxiosPromise<any>;
}

export const defaultBackendAttendee: BackendAttendee = {
  getAttendeeByApplicationUserId(id: string): AxiosPromise<Attendee> {
    let url = `${URLS.attendee}/GetSingleByApplicationUserId/${id}`;
    return instance.get<Attendee>(url);
  },

  getAttendeeByAttendeeId(attendeeId: string): AxiosPromise<Attendee> {
    let url = `${URLS.attendee}/GetSingleByAttendeeId/${attendeeId}`;
    return instance.get<Attendee>(url);
  },

  getAttendees(
    searchParams: SearchParams,
    locationId?: string,
    departmentId?: string,
    active?: boolean,
    excludeAttendeeIds?: string[],
    attendeeIds?: string[],
    filterByMembershipTypes?: string[],
    departmentIds?: string[],
    trainerId?: string,
    attendeeRoleIds?: string[],
    memberAtDate?: string,
    applicationUserId?: string
  ): AxiosPromise<Value> {
    const odfb = ODataFilterBuilder('and');

    odfb.ne(CONST.ApplicationUserId, null);
    if (active !== undefined) {
      odfb.eq(CONST.Active, active);
    }

    if (memberAtDate != undefined) {
      // In some instances the milliseconds are missing on
      // the date string and cause a BadRequest in Odata
      // Dirty workaround
      if (!memberAtDate.endsWith('Z')) {
        memberAtDate += '.000Z';
      }

      const odfbNested = ODataFilterBuilder('or');
      odfbNested.ge(CONST.TillDate, memberAtDate, false);
      odfbNested.eq(CONST.TillDate, null);

      odfb.and(odfbNested);
      odfb.le(CONST.FromDate, memberAtDate, false);
    }

    if (excludeAttendeeIds !== undefined) {
      excludeAttendeeIds.forEach((x) => odfb.ne(CONST.Id, x, false));
    }

    if (attendeeIds !== undefined) {
      const odfbInner = ODataFilterBuilder('or');
      attendeeIds.forEach((x) => odfbInner.eq(CONST.Id, x, false));
      odfb.and(odfbInner);
    }

    if (applicationUserId != undefined) {
      odfb.eq(CONST.ApplicationUserId, applicationUserId, false);
    }

    const locationIdString = locationId ? locationId : 'null';
    const trainerIdString = trainerId ? trainerId : 'null';

    const membershipTypesString = `[${
      filterByMembershipTypes?.length
        ? filterByMembershipTypes.map((id) => `'${id}'`).join(',')
        : ''
    }]`;

    const departmentsString = `[${
      departmentId
        ? `'${departmentId}'`
        : departmentIds?.length
        ? departmentIds.map((id) => `'${id}'`).join(',')
        : ''
    }]`;

    const attendeeRolesString = `[${
      attendeeRoleIds?.length
        ? attendeeRoleIds.map((id) => `'${id}'`).join(',')
        : ''
    }]`;

    const baseUrl = `${URLS.attendeeOdata}/GetByParams(locationId=${locationIdString},trainerId=${trainerIdString},departmentIds=${departmentsString},membershipTypeIds=${membershipTypesString},attendeeRoleIds=${attendeeRolesString})`;

    const url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );
    logger.log(`getAttendees${url}`);
    return instance.get<Value>(url);
  },
  getInterested(
    searchParams: SearchParams,
    locationId?: string,
    departmentId?: string
  ): AxiosPromise<Value> {
    const odfb = ODataFilterBuilder('and');

    const locationIdString = locationId ? locationId : 'null';
    const departmentsString = `[${departmentId ? `'${departmentId}'` : ''}]`;

    const baseUrl = `${URLS.attendeeOdata}/GetInterested(locationId=${locationIdString},departmentIds=${departmentsString})`;

    const url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );
    logger.log(`getInterested: ${url}`);
    return instance.get<Value>(url);
  },

  getAttendeesByAppointmentId(
    searchParams: SearchParams,
    appointmentId: string
  ): AxiosPromise<Value> {
    const odfb = ODataFilterBuilder('and');
    const url = DefaultBackendHelper.makeUrl(
      `${URLS.attendeeOdata}/GetByAppointmentId(appointmentId=${appointmentId})`,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );

    logger.log(`GetByAppointmentId: ${url}`);
    return instance.get<Value>(url);
  },

  getAttendeesWithoutMembership(
    searchParams: SearchParams,
    locationId?: string,
    departmentId?: string
  ): AxiosPromise<Value> {
    const odfb = ODataFilterBuilder('and');

    // Ensure that only active attendees are listed.
    odfb.eq(CONST.Active, true);
    const memberAtDate = new Date().toISOString();
    const odfbNested = ODataFilterBuilder('or');
    odfbNested.ge(CONST.TillDate, memberAtDate, false);
    odfbNested.eq(CONST.TillDate, null);
    odfb.and(odfbNested);
    odfb.le(CONST.FromDate, memberAtDate, false);
    // -----
    const locationIdString = locationId ? locationId : 'null';
    const departmentsString = `[${departmentId ? `'${departmentId}'` : ''}]`;

    const baseUrl = `${URLS.attendeeOdata}/GetWithoutMembership(locationId=${locationIdString},departmentIds=${departmentsString})`;

    const url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );

    logger.log(`getWithoutMembership: ${url}`);
    return instance.get<Value>(url);
  },

  getAttendeesActiveInactive(
    searchParams: SearchParams,
    locationId?: string,
    departmentId?: string
  ): AxiosPromise<Value> {
    const locationIdString = locationId ? locationId : 'null';
    const departmentIdString = departmentId ? departmentId : 'null';

    const baseUrl = `${URLS.attendeeOdata}/GetActiveInactive(locationId=${locationIdString},departmentId=${departmentIdString})`;

    const url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter
    );

    logger.log(`getAttendeesActiveInactive: ${url}`);
    return instance.get<Value>(url);
  },

  getDistinctAppUsers(
    searchParams: SearchParams,
    onlyActive?: boolean
  ): AxiosPromise<ApplicationUserValue> {
    const odfb = ODataFilterBuilder('and');

    if (onlyActive) {
      odfb.eq(CONST.Active, true);
    }

    const url = DefaultBackendHelper.makeUrl(
      `${URLS.attendeeOdata}/GetDistinctAppUsers()`,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );

    return instance.get<ApplicationUserValue>(url);
  },

  getAttendeesAndInterested(
    searchParams: SearchParams,
    locationId?: string,
    active?: boolean,
    attendeeIds?: string[],
    attendeeRoleIds?: string[],
    memberAtDate?: string
  ): AxiosPromise<Value> {
    const odfb = ODataFilterBuilder('and');

    if (active !== undefined) {
      const odfbInner = ODataFilterBuilder('or');
      odfbInner.eq(CONST.Active, active);
      odfbInner.eq(CONST.Role, AUTHORITIES.INTERESTED);
      odfb.and(odfbInner);
    }

    if (attendeeIds !== undefined) {
      const odfbInner = ODataFilterBuilder('or');
      attendeeIds.forEach((x) => odfbInner.eq(CONST.Id, x, false));
      odfb.and(odfbInner);
    }

    if (memberAtDate != undefined) {
      // In some instances the milliseconds are missing on
      // the date string and cause a BadRequest in Odata
      // Dirty workaround
      if (!memberAtDate.endsWith('Z')) {
        memberAtDate += '.000Z';
      }

      const odfbNested = ODataFilterBuilder('or');
      odfbNested.ge(CONST.TillDate, memberAtDate, false);
      odfbNested.eq(CONST.TillDate, null);

      odfb.and(odfbNested);
      odfb.le(CONST.FromDate, memberAtDate, false);
    }

    const locationIdString = locationId ? locationId : 'null';

    const attendeeRolesString = `[${
      attendeeRoleIds?.length
        ? attendeeRoleIds.map((id) => `'${id}'`).join(',')
        : ''
    }]`;

    const baseUrl = `${URLS.attendeeOdata}/GetAttendeesAndInterested(locationId=${locationIdString},attendeeRoleIds=${attendeeRolesString})`;

    const url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );
    logger.log(`getAttendeesAndInterested: ${url}`);
    return instance.get<Value>(url);
  },

  getAttendeesBySearch(
    search?: string,
    locationIds?: string[],
    departmentIds?: string[],
    courseIds?: string[],
    courseFromDate?: string,
    courseTillDate?: string,
    membershipTypeIds?: string[]
  ) {
    const url = `${URLS.attendee}/GetAttendeesBySearch`;
    return instance.post<Attendee[]>(url, {
      Search: search,
      LocationIds: locationIds,
      DepartmentIds: departmentIds,
      CourseIds: courseIds,
      CourseFromDate: courseFromDate,
      CourseTillDate: courseTillDate,
      MembershipTypeIds: membershipTypeIds,
    });
  },

  deleteAttendee(id: string): AxiosPromise {
    logger.debug('deleteAttendee');
    return instance.delete(`${URLS.attendee}/${id}`);
  },
  deleteInterestedAttendee(id: string): AxiosPromise {
    logger.debug('deleteInterestedAttendee');
    return instance.delete(`${URLS.attendee}/DeleteInterested/${id}`);
  },

  updateAttendee(
    Attendee: Attendee,
    attendeeFixedCosts?: AttendeeFixedCost[],
    departmentIds?: string[]
  ): AxiosPromise<any> {
    const payload = attendee.toAPI(Attendee);
    payload.AttendeeFixedCosts =
      attendeeFixedCosts?.map(attendeeFixedCost.toAPI) ?? null;
    payload.DepartmentIds = departmentIds ?? null;

    if (!isEmptyId(Attendee.id)) {
      return instance.put<Attendee>(
        `${URLS.attendee}/${i18nGlobal.locale.value}`,
        payload
      );
    } else {
      return instance.post<Attendee>(
        `${URLS.attendee}/${i18nGlobal.locale.value}`,
        payload
      );
    }
  },
  updateInterestedAttendee(Attendee: Attendee): AxiosPromise<any> {
    logger.debug(`${URLS.attendee}/UpdateInterested/${Attendee.id}`);
    return instance.put<Attendee>(
      `${URLS.attendee}/UpdateInterested/${Attendee.id}`,
      attendee.toAPI(Attendee)
    );
  },
  registerAttendee(
    Attendee: ApplicationUser,
    locationId: string
  ): AxiosPromise<any> {
    logger.debug('updateAttendee');
    return instance.post<Attendee>(
      `${URLS.account}/RegisterAttendee/${i18nGlobal.locale.value}/${locationId}`,
      {
        FirstName: Attendee.firstName,
        LastName: Attendee.lastName,
        Email: Attendee.email,
        UserName: Attendee.userName,
        Password: Attendee.password,
      }
    );
  },
  registerInterestedAttendeeByEmployee(
    Attendee: Attendee,
    locationId: string
  ): AxiosPromise<any> {
    logger.debug(`${URLS.attendee}/AddInterested/${locationId}`);
    return instance.post<Attendee>(
      `${URLS.attendee}/AddInterested/${locationId}`,
      attendee.toAPI(Attendee)
    );
  },
  checkIfAttendeeHasFutureBookings(attendeeId: string): AxiosPromise<boolean> {
    const url = `${URLS.attendee}/CheckIfAttendeeHasFutureBookings/${attendeeId}`;
    return instance.get<boolean>(url);
  },
  getLocationInfoById(id: string): AxiosPromise<Location> {
    const url = `${URLS.attendeeRegisterByQrCode}/GetLocationInfoById/${id}`;
    return instance.get<Location>(url);
  },
  requestNewMemberNumber(): AxiosPromise<string> {
    const url = `${URLS.attendee}/GetNextMemberNumber`;
    return instance.get<string>(url);
  },
  exportAttendees(
    searchParams: SearchParams,
    culture: string,
    locationId?: string,
    departmentId?: string,
    filterByMembershipTypes?: string[],
    departmentIds?: string[]
  ): AxiosPromise<any> {
    const url = `${URLS.attendee}/Export?culture=${culture}`;
    const payload = {
      LocationId: locationId,
      DepartmentIds: departmentId ? [departmentId] : departmentIds,
      MembershipTypeIds: filterByMembershipTypes,
      TextFilter: searchParams.filter,
      SortBy: searchParams.dataOption.sortBy,
      SortDesc: searchParams.dataOption.sortDesc,
    };

    return instance.post<any>(url, payload, { responseType: 'blob' });
  },
};
